// Editable · Checkbox
//
// Wraps AntD <Checkbox> with the DS token theme and a clean Input/Output
// contract. The reason this wrapper exists (vs. importing AntD Checkbox
// directly): checkboxes routinely sit inside clickable rows/cards, and a raw
// AntD Checkbox bubbles its click to the ancestor's onClick — selecting the
// row while (un)checking the box. This wrapper defaults `stopPropagation`
// so a checkbox inside a clickable ancestor never triggers it. Callers that
// WANT the click to bubble opt out via `stopPropagation={false}`.
//
// Form.Item binding: transparent to `valuePropName="checked"` — spreads
// `checked` + `onChange` straight through. `data-testid` lands on the inner
// <input type="checkbox"> (via AntD rest-props) so `getByTestId(x).check()`
// works.

import type React from "react";
import type { ReactNode } from "react";
import { Checkbox as AntCheckbox } from "antd";

// ─── Inputs (data props — the model) ─────────────────────────────────────────

export interface CheckboxInput {
  /**
   * Controlled checked state. Optional so AntD `Form.Item` (with
   * `valuePropName="checked"`) can inject it at runtime.
   */
  checked?: boolean;
  /** Disable the checkbox. Default false. */
  disabled?: boolean;
  /** Auto-focus on mount — for inline-edit affordances. */
  autoFocus?: boolean;
  /** Label rendered beside the box (as the AntD Checkbox children). */
  label?: ReactNode;
  /**
   * Stop the click from bubbling to a clickable ancestor (row/card). Default
   * true — the raison d'être of this wrapper. Set false to let the click
   * propagate.
   */
  stopPropagation?: boolean;
  /**
   * `data-testid` on the inner `<input type="checkbox">` element, so
   * `getByTestId(x).check()` toggles it.
   */
  testId?: string;
}

// ─── Outputs (events emitted — controller hooks) ──────────────────────────────

export interface CheckboxOutput {
  /** Fires with the new checked state on toggle. */
  onChange?: (checked: boolean) => void;
}

export type CheckboxProps = CheckboxInput & CheckboxOutput;

// ─── Component ────────────────────────────────────────────────────────────────

export function Checkbox({
  checked,
  disabled = false,
  autoFocus,
  label,
  stopPropagation = true,
  testId,
  onChange,
}: CheckboxProps) {
  return (
    <AntCheckbox
      checked={checked}
      disabled={disabled}
      autoFocus={autoFocus}
      onChange={(e) => onChange?.(e.target.checked)}
      onClick={(e) => {
        if (stopPropagation) e.stopPropagation();
      }}
      // `data-testid` flows through AntD's rest-props onto the inner
      // <input type="checkbox"> — reachable + checkable for e2e.
      {...(testId ? { "data-testid": testId } : {})}
    >
      {label}
    </AntCheckbox>
  );
}
